import React, { useState, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  flexRender,
} from '@tanstack/react-table';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { InfoTooltip } from '@/components/ui/info-tooltip';
import screeningService from '@/services/screeningService';
import { useToast } from '@/hooks/use-toast';
import { ArrowUpDown, ArrowUp, ArrowDown, Filter, PlayCircle, Plus, Loader2, TrendingUp } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

// Infobulles pour le screening
const SCREENING_TOOLTIPS = {
  screening: "Outil de filtrage pour trouver des actions selon vos critères : capitalisation, secteur, ratios financiers, performance, etc.",
  compositeScore: "Score global combinant l'analyse fondamentale (40%), le risque pays (25%), les indicateurs techniques (15%) et le secteur (20%).",
  peRatio: "Price-to-Earnings : prix de l'action divisé par le bénéfice par action. Élevé = cher ou forte croissance attendue. Bas = bon marché ou croissance faible.",
  dividendYield: "Rendement du dividende : dividende annuel divisé par le prix de l'action. Exprimé en %.",
  marketCap: "Capitalisation boursière : valeur totale de l'entreprise en bourse. Large cap > 10Md$, Mid cap 2-10Md$, Small cap < 2Md$.",
  rsi: "Relative Strength Index : indicateur technique (0-100). RSI > 70 = suracheté, RSI < 30 = survendu.",
  minScore: "Score minimum requis pour afficher une action. Plus le score est élevé, plus l'action est bien notée selon nos critères.",
};

const ScoreBar = ({ score }) => {
  const getColor = (score) => {
    if (score >= 70) return 'bg-green-500';
    if (score >= 40) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  const getTextColor = (score) => {
    if (score >= 70) return 'text-green-700 dark:text-green-400';
    if (score >= 40) return 'text-yellow-700 dark:text-yellow-400';
    return 'text-red-700 dark:text-red-400';
  };

  return (
    <div className="flex items-center gap-2">
      <div className="w-24 h-4 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
        <div
          className={`h-full ${getColor(score)} transition-all duration-500`}
          style={{ width: `${score}%` }}
        ></div>
      </div>
      <span className={`text-sm font-semibold ${getTextColor(score)}`}>
        {score.toFixed(1)}
      </span>
    </div>
  );
};

const SectorBadge = ({ sector }) => {
  const colors = {
    Technology: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
    Finance: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
    Consumer: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
    Energy: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
    Materials: 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200',
    Automotive: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
  };

  return (
    <Badge className={`${colors[sector] || colors.Technology} font-medium`}>
      {sector}
    </Badge>
  );
};

const DetailModal = ({ stock, isOpen, onClose }) => {
  if (!stock) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto bg-slate-900 border-slate-700 text-white">
        <DialogHeader>
          <DialogTitle className="text-2xl text-white">
            {stock.country} {stock.name} ({stock.ticker})
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Overall Score */}
          <Card className="p-4 bg-gradient-to-r from-blue-900/40 to-indigo-900/40 border-blue-500/30">
            <h3 className="text-lg font-semibold mb-2 text-white">Composite Score</h3>
            <ScoreBar score={stock.composite_score} />
          </Card>

          {/* Score Breakdown */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <h3 className="text-lg font-semibold text-white">Score Breakdown</h3>
              <InfoTooltip content={SCREENING_TOOLTIPS.compositeScore} />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <Card className="p-3 bg-slate-800 border-slate-700">
                <p className="text-sm text-gray-400 mb-1">Fundamental (40%)</p>
                <p className="text-2xl font-bold text-blue-400">
                  {stock.scores.fundamental.toFixed(1)}
                </p>
              </Card>
              
              <Card className="p-3 bg-slate-800 border-slate-700">
                <p className="text-sm text-gray-400 mb-1">Country Risk (25%)</p>
                <p className="text-2xl font-bold text-green-400">
                  {stock.scores.country.toFixed(1)}
                </p>
              </Card>
              
              <Card className="p-3 bg-slate-800 border-slate-700">
                <p className="text-sm text-gray-400 mb-1">Technical (15%)</p>
                <p className="text-2xl font-bold text-purple-400">
                  {stock.scores.technical.toFixed(1)}
                </p>
              </Card>
              
              <Card className="p-3 bg-slate-800 border-slate-700">
                <p className="text-sm text-gray-400 mb-1">Sector (20%)</p>
                <p className="text-2xl font-bold text-orange-400">
                  {stock.scores.sector.toFixed(1)}
                </p>
              </Card>
            </div>
          </div>

          {/* Fundamentals */}
          <div className="space-y-2">
            <h3 className="text-lg font-semibold text-white">Fundamental Metrics</h3>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div className="flex justify-between p-2 bg-slate-800 rounded">
                <span className="text-gray-400">Price:</span>
                <span className="font-semibold text-white">${stock.price}</span>
              </div>
              <div className="flex justify-between items-center p-2 bg-slate-800 rounded">
                <span className="text-gray-400 flex items-center gap-1">
                  P/E Ratio <InfoTooltip content={SCREENING_TOOLTIPS.peRatio} />
                </span>
                <span className="font-semibold text-white">{stock.pe_ratio || 'N/A'}</span>
              </div>
              <div className="flex justify-between p-2 bg-slate-800 rounded">
                <span className="text-gray-400">EPS:</span>
                <span className="font-semibold text-white">${stock.eps || 'N/A'}</span>
              </div>
              <div className="flex justify-between items-center p-2 bg-slate-800 rounded">
                <span className="text-gray-400 flex items-center gap-1">
                  Dividend Yield <InfoTooltip content={SCREENING_TOOLTIPS.dividendYield} />
                </span>
                <span className="font-semibold text-white">{stock.dividend_yield}%</span>
              </div>
              <div className="flex justify-between p-2 bg-slate-800 rounded">
                <span className="text-gray-400">P/B Ratio:</span>
                <span className="font-semibold text-white">{stock.pb_ratio || 'N/A'}</span>
              </div>
              <div className="flex justify-between items-center p-2 bg-slate-800 rounded">
                <span className="text-gray-400 flex items-center gap-1">
                  Market Cap <InfoTooltip content={SCREENING_TOOLTIPS.marketCap} />
                </span>
                <span className="font-semibold text-white">
                  ${(stock.market_cap / 1e9).toFixed(2)}B
                </span>
              </div>
            </div>
          </div>

          {/* Technical */}
          <div className="space-y-2">
            <h3 className="text-lg font-semibold text-white">Technical Indicators</h3>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div className="flex justify-between items-center p-2 bg-slate-800 rounded">
                <span className="text-gray-400 flex items-center gap-1">
                  RSI <InfoTooltip content={SCREENING_TOOLTIPS.rsi} />
                </span>
                <span className="font-semibold text-white">{stock.rsi.toFixed(2)}</span>
              </div>
              <div className="flex justify-between p-2 bg-slate-800 rounded">
                <span className="text-gray-400">50-Day MA:</span>
                <span className="font-semibold text-white">${stock.ma_50.toFixed(2)}</span>
              </div>
              <div className="flex justify-between p-2 bg-slate-800 rounded">
                <span className="text-gray-400">Price vs MA:</span>
                <span className={`font-semibold ${
                  stock.price_vs_ma >= 0 ? 'text-green-400' : 'text-red-400'
                }`}>
                  {stock.price_vs_ma.toFixed(2)}%
                </span>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const ScreeningTable = () => {
  const location = useLocation();
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [sorting, setSorting] = useState([{ id: 'composite_score', desc: true }]);
  const [selectedStock, setSelectedStock] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedStocks, setSelectedStocks] = useState([]);
  
  // Filters
  const [regionFilter, setRegionFilter] = useState('all');
  const [sectorFilter, setSectorFilter] = useState('all');
  const [minScore, setMinScore] = useState(0);
  
  const { toast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    loadScreeningData();
    
    // Check if country filter was passed from map
    if (location.state?.filterCountry) {
      const countryCode = location.state.filterCountry;
      // Map country codes to regions
      const countryToRegion = {
        'USA': 'US',
        'NLD': 'Europe',
        'FRA': 'Europe',
        'DEU': 'Europe',
        'CHE': 'Europe',
        'GBR': 'Europe',
        'TWN': 'Asia',
        'KOR': 'Asia',
        'JPN': 'Asia',
        'CHN': 'Asia',
        'BRA': 'Emerging',
        'IND': 'Emerging'
      };
      const region = countryToRegion[countryCode];
      if (region) {
        setRegionFilter(region);
        toast({
          title: 'Filter Applied',
          description: `Showing stocks from selected country`,
        });
      }
    }
  }, [location.state]);

  const loadScreeningData = async () => {
    try {
      setIsLoading(true);
      const response = await screeningService.getScreeningData();
      setData(response.stocks || []);
      toast({
        title: 'Success',
        description: `Loaded ${response.count} stocks`,
      });
    } catch (error) {
      console.error('Error loading screening data:', error);
      toast({
        title: 'Error',
        description: 'Failed to load screening data',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const filteredData = useMemo(() => {
    return data.filter(stock => {
      if (regionFilter !== 'all' && stock.region !== regionFilter) return false;
      if (sectorFilter !== 'all' && stock.sector !== sectorFilter) return false;
      if (stock.composite_score < minScore) return false;
      return true;
    });
  }, [data, regionFilter, sectorFilter, minScore]);

  const columns = useMemo(
    () => [
      {
        accessorKey: 'ticker',
        header: 'Ticker',
        cell: ({ row }) => (
          <div className="font-mono font-semibold text-white">{row.original.ticker}</div>
        ),
      },
      {
        accessorKey: 'name',
        header: 'Name',
        cell: ({ row }) => (
          <div>
            <div className="font-medium text-white">{row.original.name}</div>
            <div className="text-xs text-gray-400">
              {row.original.country} {row.original.region}
            </div>
          </div>
        ),
      },
      {
        accessorKey: 'sector',
        header: 'Sector',
        cell: ({ row }) => <SectorBadge sector={row.original.sector} />,
      },
      {
        accessorKey: 'price',
        header: 'Price',
        cell: ({ row }) => (
          <span className="text-white">${row.original.price.toFixed(2)}</span>
        ),
      },
      {
        accessorKey: 'composite_score',
        header: () => (
          <span className="flex items-center gap-1.5">
            Score
            <InfoTooltip content={SCREENING_TOOLTIPS.compositeScore} />
          </span>
        ),
        cell: ({ row }) => <ScoreBar score={row.original.composite_score} />,
      },
      {
        id: 'actions',
        header: 'Actions',
        cell: ({ row }) => (
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                setSelectedStock(row.original);
                setIsModalOpen(true);
              }}
            >
              Details
            </Button>
            <Button
              size="sm"
              variant={selectedStocks.includes(row.original.ticker) ? 'default' : 'outline'}
              onClick={() => {
                if (selectedStocks.includes(row.original.ticker)) {
                  setSelectedStocks(selectedStocks.filter(t => t !== row.original.ticker));
                } else {
                  setSelectedStocks([...selectedStocks, row.original.ticker]);
                }
              }}
            >
              <Plus className="w-3 h-3 mr-1" />
              {selectedStocks.includes(row.original.ticker) ? 'Added' : 'Add'}
            </Button>
          </div>
        ),
      },
    ],
    [selectedStocks]
  );

  const table = useReactTable({
    data: filteredData,
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  const handleBacktestTop5 = () => {
    const top5 = filteredData.slice(0, 5).map(s => s.ticker);
    navigate('/backtest', { state: { selectedStocks: top5 } });
  };

  const handleBacktestSelected = () => {
    if (selectedStocks.length === 0) {
      toast({
        title: 'No Stocks Selected',
        description: 'Please select at least one stock',
        variant: 'destructive',
      });
      return;
    }
    navigate('/backtest', { state: { selectedStocks } });
  };

  return (
    <div className="space-y-6">
      {/* Filters */}
      <Card className="p-6 bg-[#1a1a2e] border-[#3a3a4e]">
        <div className="flex items-center gap-2 mb-4">
          <Filter className="w-5 h-5 text-cyan-400" />
          <h3 className="text-lg font-semibold text-white">Filters</h3>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="text-sm font-medium mb-2 block text-gray-300">Region</label>
            <Select value={regionFilter} onValueChange={setRegionFilter}>
              <SelectTrigger className="bg-[#2a2a3e] border-[#3a3a4e] text-white focus:ring-cyan-400">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#2a2a3e] border-[#3a3a4e]">
                <SelectItem value="all" className="text-white hover:bg-[#3a3a4e]">All Regions</SelectItem>
                <SelectItem value="US" className="text-white hover:bg-[#3a3a4e]">United States</SelectItem>
                <SelectItem value="Europe" className="text-white hover:bg-[#3a3a4e]">Europe</SelectItem>
                <SelectItem value="Asia" className="text-white hover:bg-[#3a3a4e]">Asia</SelectItem>
                <SelectItem value="Emerging" className="text-white hover:bg-[#3a3a4e]">Emerging Markets</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="text-sm font-medium mb-2 block text-gray-300">Sector</label>
            <Select value={sectorFilter} onValueChange={setSectorFilter}>
              <SelectTrigger className="bg-[#2a2a3e] border-[#3a3a4e] text-white focus:ring-cyan-400">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#2a2a3e] border-[#3a3a4e]">
                <SelectItem value="all" className="text-white hover:bg-[#3a3a4e]">All Sectors</SelectItem>
                <SelectItem value="Technology" className="text-white hover:bg-[#3a3a4e]">Technology</SelectItem>
                <SelectItem value="Finance" className="text-white hover:bg-[#3a3a4e]">Finance</SelectItem>
                <SelectItem value="Consumer" className="text-white hover:bg-[#3a3a4e]">Consumer</SelectItem>
                <SelectItem value="Energy" className="text-white hover:bg-[#3a3a4e]">Energy</SelectItem>
                <SelectItem value="Materials" className="text-white hover:bg-[#3a3a4e]">Materials</SelectItem>
                <SelectItem value="Automotive" className="text-white hover:bg-[#3a3a4e]">Automotive</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="text-sm font-medium mb-2 block text-gray-300 flex items-center gap-1.5">
              Min Score: {minScore}
              <InfoTooltip content={SCREENING_TOOLTIPS.minScore} />
            </label>
            <input
              type="range"
              min="0"
              max="100"
              value={minScore}
              onChange={(e) => setMinScore(Number(e.target.value))}
              className="w-full h-2 bg-[#3a3a4e] rounded-lg appearance-none cursor-pointer slider-thumb-cyan"
              style={{
                background: `linear-gradient(to right, #00d4ff 0%, #00d4ff ${minScore}%, #3a3a4e ${minScore}%, #3a3a4e 100%)`
              }}
            />
          </div>

          <div className="flex items-end">
            <Button 
              onClick={loadScreeningData} 
              className="w-full bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white"
            >
              Refresh Data
            </Button>
          </div>
        </div>
      </Card>

      {/* Action Buttons */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-cyan-400" />
          <span className="font-semibold text-white">
            {filteredData.length} stocks • {selectedStocks.length} selected
          </span>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={handleBacktestTop5}
            disabled={filteredData.length === 0}
            className="bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white transition-all hover:scale-105 hover:shadow-lg"
          >
            <PlayCircle className="w-4 h-4 mr-2" />
            Backtest Top 5
          </Button>
          <Button
            onClick={handleBacktestSelected}
            disabled={selectedStocks.length === 0}
            variant="outline"
            className="border-cyan-500 text-cyan-500 hover:bg-cyan-500 hover:text-white transition-all hover:scale-105"
          >
            <PlayCircle className="w-4 h-4 mr-2" />
            Backtest Selected ({selectedStocks.length})
          </Button>
        </div>
      </div>

      {/* Table */}
      <Card className="overflow-hidden bg-[#1a1a2e] border-[#3a3a4e]">
        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-12 h-12 text-cyan-400 animate-spin" />
          </div>
        ) : (
          <div className="overflow-x-auto mobile-scroll">
            <table className="w-full">
              <thead className="bg-[#1a1a2e] border-b border-[#3a3a4e]">
                {table.getHeaderGroups().map(headerGroup => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map(header => (
                      <th
                        key={header.id}
                        className="px-4 py-3 text-left text-sm font-semibold text-white cursor-pointer hover:bg-[#2a2a3e]"
                        onClick={header.column.getToggleSortingHandler()}
                      >
                        <div className="flex items-center gap-2">
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                          {header.column.getIsSorted() && (
                            header.column.getIsSorted() === 'desc' ? (
                              <ArrowDown className="w-4 h-4" />
                            ) : (
                              <ArrowUp className="w-4 h-4" />
                            )
                          )}
                        </div>
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody className="divide-y divide-[#3a3a4e]">
                {table.getRowModel().rows.map((row, index) => (
                  <tr
                    key={row.id}
                    className={`transition-colors hover:bg-[#2a2a3e] ${
                      index % 2 === 0 ? 'bg-[#1a1a2e]' : 'bg-[#242438]'
                    }`}
                  >
                    {row.getVisibleCells().map(cell => (
                      <td key={cell.id} className="px-4 py-3 text-sm text-white">
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Detail Modal */}
      <DetailModal
        stock={selectedStock}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
    </div>
  );
};

export default ScreeningTable;
